import React, { useState } from "react";
import {
  Box,
  Grid,
  Card,
  CardHeader,
  CardContent,
  CardActions,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Container,
  Button,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

export const Pricing = () => {
  // Updated plans with new color palette
  const plans = [
    {
      name: "BASIC PACKAGE",
      icon: "",
      price: "30,000 - ₹ 2,00,000",
      currency: "₹",
      priceLabel: "Starting at",
      color: "#58A0C8", // Lowest price - lightest blue
      features: ["Static website (up to 5 pages)", "1 Month Support"],
    },
    {
      name: "GROWTH PACKAGE",
      icon: "",
      price: "75,000 - ₹ 5,00,000",
      currency: "₹",
      priceLabel: "Starting at",
      color: "#34699A", // Middle price - medium blue
      features: ["Dynamic Web App", "2 Integrations", "3 Months Support"],
    },
  ];

  return (
    <Container maxWidth="lg" sx={{ py: 6, mb: 10 }}>
      {/* Header Section */}
      <Box textAlign="center" mb={6} mt={14}>
        <Typography
          variant="h3"
          sx={{
            fontWeight: "bold",
            mb: 2,
            color: "black",
            fontFamily: "Plus Jakarta Sans",
          }}
        >
          Choose Your Package
        </Typography>
        <Typography
          variant="h6"
          color="text.secondary"
          sx={{
            maxWidth: 600,
            mx: "auto",
            fontFamily: "Plus Jakarta Sans",
          }}
        >
          Select the perfect plan for your business needs. All packages include
          professional development and ongoing support.
        </Typography>
      </Box>

      {/* Pricing Cards Grid */}
      <Grid container spacing={4} justifyContent="center">
        {plans.map((plan, index) => (
          <Grid item xs={12} md={4} key={plan.name}>
            <EnhancedPricingCard plan={plan} />
          </Grid>
        ))}
      </Grid>
      <Typography
        sx={{
          textAlign: "center",
          mt: 5,
          fontWeight: "bold",
          fontSize: "16px",
          fontFamily: "Plus Jakarta Sans",
        }}
      >
        Note* : All Payments are Final. No Refunds/Cancellations are
        Entertained.
      </Typography>
    </Container>
  );
};

// Enhanced Individual Pricing Card Component
const EnhancedPricingCard = ({ plan }) => {
  const [isHovered, setIsHovered] = useState(false);

  const handleButtonClick = (e) => {
    e.stopPropagation();
    // Handle button click logic based on plan type
    if (plan.price === "Custom Quote") {
      // Scroll to element with id "sec1-prt2" for enterprise package
      const element = document.getElementById("sec1-prt2");
      if (element) {
        const elementPosition = element.offsetTop;
        const offsetPosition = elementPosition - 100; // 100px offset from top

        window.scrollTo({
          top: offsetPosition,
          behavior: "smooth",
        });
      }
    } else {
      // Purchase plan
      window.open("/checkout", "_blank");
    }
  };

  return (
    <Card
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        boxShadow: isHovered ? 8 : 4,
        borderRadius: 3,
        overflow: "hidden",
        position: "relative",
        transform: isHovered
          ? "translateY(-8px) scale(1.02)"
          : "translateY(0) scale(1)",
        transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
        border: "1px solid #e0e0e0",
      }}
    >
      {/* Fixed Height Header */}
      <CardHeader
        title={
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 1.5,
            }}
          >
            <Typography
              component="span"
              sx={{
                fontSize: "2rem",
                filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.3))",
                fontFamily: "Plus Jakarta Sans",
              }}
            >
              {plan.icon}
            </Typography>
            <Typography
              variant="h5"
              sx={{
                fontWeight: "bold",
                fontSize: "1.3rem",
                letterSpacing: "0.5px",
                textShadow: "0 1px 2px rgba(0,0,0,0.3)",
                fontFamily: "Plus Jakarta Sans",
              }}
            >
              {plan.name}
            </Typography>
          </Box>
        }
        sx={{
          background: `linear-gradient(135deg, ${plan.color}, ${plan.color}dd)`,
          color: "white",
          textAlign: "center",
          height: 80, // Fixed header height
          display: "flex",
          alignItems: "center",
          position: "relative",
          "&::after": {
            content: '""',
            position: "absolute",
            bottom: 0,
            left: 0,
            right: 0,
            height: "1px",
            background: "rgba(255,255,255,0.2)",
          },
        }}
      />

      {/* Features Content */}
      <CardContent
        sx={{
          flexGrow: 1,
          px: 3,
          py: 4,
          backgroundColor: "#fafafa",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <List dense sx={{ p: 0, flexGrow: 1 }}>
          {plan.features.map((feature, index) => (
            <ListItem
              key={index}
              sx={{
                px: 0,
                py: 2,
                alignItems: "flex-start",
                borderRadius: 1,
                mb: 1,
                "&:hover": {
                  backgroundColor: "rgba(56, 161, 105, 0.08)",
                  transform: "translateX(4px)",
                  transition: "all 0.2s ease",
                },
              }}
            >
              <ListItemIcon sx={{ minWidth: 40, mt: 0.5 }}>
                <CheckCircleIcon
                  sx={{
                    color: plan.color,
                    fontSize: "1.4rem",
                    filter: "drop-shadow(0 1px 2px rgba(0,0,0,0.2))",
                  }}
                />
              </ListItemIcon>
              <ListItemText
                primary={
                  <Typography
                    variant="body1"
                    sx={{
                      fontSize: "1rem",
                      lineHeight: 1.6,
                      color: "#2D3748",
                      fontWeight: 500,
                      fontFamily: "Plus Jakarta Sans",
                    }}
                  >
                    {feature}
                  </Typography>
                }
              />
            </ListItem>
          ))}
        </List>
      </CardContent>

      {/* Fixed Height Footer */}
      <CardActions
        sx={{
          background: `linear-gradient(135deg, ${plan.color}, ${plan.color}dd)`,
          color: "white",
          justifyContent: "center",
          height: 140, // Fixed footer height
          mt: "auto",
          flexDirection: "column",
          gap: 2,
          display: "flex",
          alignItems: "center",
        }}
      >
        <Box textAlign="center">
          {plan.priceLabel && (
            <Typography
              variant="body2"
              sx={{
                fontSize: "0.9rem",
                fontWeight: "normal",
                opacity: 0.9,
                textTransform: "uppercase",
                letterSpacing: "0.5px",
                fontFamily: "Plus Jakarta Sans",
              }}
            >
              {plan.priceLabel}
            </Typography>
          )}
          <Typography
            variant="h3"
            sx={{
              fontWeight: "bold",
              fontSize: "1.3rem",
              display: "flex",
              alignItems: "baseline",
              justifyContent: "center",
              gap: 0.5,
              textShadow: "0 2px 4px rgba(0,0,0,0.3)",
              mb: 1,
              fontFamily: "Plus Jakarta Sans",
            }}
          >
            {plan.currency && (
              <Typography
                component="span"
                sx={{
                  fontSize: "1.2rem",
                  fontWeight: "bold",
                  fontFamily: "Plus Jakarta Sans",
                }}
              >
                {plan.currency}
              </Typography>
            )}
            {plan.price}
          </Typography>
        </Box>

        {/* Improved Button */}
        {plan?.buttonText && (
          <Button
            variant="contained"
            onClick={handleButtonClick}
            sx={{
              backgroundColor: "white",
              color: plan.color,
              fontWeight: "bold",
              fontSize: "0.95rem",
              px: 4,
              py: 1.2,
              borderRadius: 2,
              textTransform: "none",
              boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
              border: `2px solid transparent`,
              minWidth: 140,
              fontFamily: "Plus Jakarta Sans",
              "&:hover": {
                backgroundColor: "#f8f8f8",
                transform: "translateY(-1px)",
                boxShadow: "0 4px 12px rgba(0,0,0,0.25)",
                border: `2px solid white`,
              },
            }}
          >
            {plan.buttonText}
          </Button>
        )}
      </CardActions>
    </Card>
  );
};
